import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { Appointment } from '@salonomy/types';
import { Button, IconButton, SegmentedControl, Select, TextInput } from '@salonomy/ui';
import { useAppointments, isOptimistic, useUpdateAppointment } from '../data/appointments.js';
import { useQueue } from '../data/queue.js';
import { chairStaff, useSalon, useStaff } from '../data/salon.js';
import { useServices } from '../data/services.js';
import { useDayCalendar } from '../data/scheduling.js';
import { useAuth } from '../lib/auth.js';
import { longDate, todayInZone, wallMinutes, wallMinutesToUtcIso } from '../lib/time.js';
import { chairAccent } from '../features/diary/accents.js';
import { AppointmentModal } from '../features/diary/AppointmentModal.js';
import { BlockOutModal } from '../features/diary/BlockOutModal.js';
import { resolveColumnCalendar } from '../features/diary/calendar.js';
import { DiaryGrid, type DiaryColumn, type MoveChange } from '../features/diary/DiaryGrid.js';
import { NewAppointmentModal } from '../features/diary/NewAppointmentModal.js';
import { OfferGapModal } from '../features/diary/OfferGapModal.js';
import { WeekStrip } from '../features/diary/WeekStrip.js';
import { assignLanes, computeRange, findGaps } from '../features/diary/layout.js';
import { QueueRail } from '../features/queue/QueueRail.js';
import { JoinWaitlistButton } from '../features/waitlist/JoinWaitlist.js';
import { WaitlistDepthBadge } from '../features/waitlist/WaitlistDepth.js';

type View = 'day' | 'week' | 'staff';
const MIN_OFFERABLE_GAP = 30;

export function DiaryPage() {
  const { isFloorManager } = useAuth();
  const { data: salon } = useSalon();
  const tz = salon?.timezone ?? 'Europe/London';
  const today = todayInZone(tz);

  const [date, setDate] = useState(today);
  const [view, setView] = useState<View>('day');
  const [focusStaffId, setFocusStaffId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [gapTarget, setGapTarget] = useState<{
    staffId: string;
    startMin: number;
    endMin: number;
  } | null>(null);
  const [openAppt, setOpenAppt] = useState<Appointment | null>(null);
  const [blockOutStaffId, setBlockOutStaffId] = useState<string | null>(null);

  const { data: staff } = useStaff();
  const { data: services } = useServices();
  const { data: appts = [] } = useAppointments(date, salon?.timezone);
  const { data: queue } = useQueue();
  const updateAppt = useUpdateAppointment(date);

  const chairs = chairStaff(staff);
  // WS7 calendar realism — the day's closed-date, per-staff time-off and hour-exceptions.
  const calendar = useDayCalendar(date, chairs);
  const isClosed = calendar.closedDate != null;

  // WS7: per-staff off/band resolution for the viewed date, reused by the columns and the
  // New-appointment modal so the diary never offers a booking path the salon can't actually take.
  const staffCalendar = useMemo(
    () =>
      new Map(
        chairs.map((m) => [
          m.id,
          resolveColumnCalendar(
            date,
            calendar.timeOffByStaff.get(m.id) ?? [],
            calendar.exceptionsByStaff.get(m.id) ?? [],
          ),
        ]),
      ),
    [chairs, date, calendar],
  );
  // Staff a booking can actually be created for this date: not on time-off / closed. (When the whole
  // salon is closed there are none.) Carries the adjusted working band so the modal can clamp times.
  const bookableChairs = useMemo(
    () =>
      isClosed
        ? []
        : chairs
            .filter((m) => !staffCalendar.get(m.id)?.off)
            .map((m) => ({ staff: m, band: staffCalendar.get(m.id)?.band ?? null })),
    [chairs, staffCalendar, isClosed],
  );
  const serviceMap = useMemo(() => new Map((services ?? []).map((s) => [s.id, s])), [services]);
  const waiting = queue?.waitingCount ?? 0;
  const nowMin = wallMinutes(new Date(), tz);
  const isToday = date === today;

  const allColumns = useMemo<DiaryColumn[]>(() => {
    return chairs.map((member, i) => {
      const mine = appts
        .filter((a) => a.staffId === member.id && a.status !== 'cancelled')
        .map((a) => ({
          a,
          startMin: wallMinutes(a.startAt, tz),
          endMin: wallMinutes(a.endAt, tz),
        }))
        .sort((x, y) => x.startMin - y.startMin);

      const lanes = assignLanes(mine.map((m) => ({ startMin: m.startMin, endMin: m.endMin })));
      const items = mine.map((m, idx) => ({
        id: m.a.id,
        appt: m.a,
        startMin: m.startMin,
        endMin: m.endMin,
        lane: lanes[idx]!.lane,
        laneCount: lanes[idx]!.laneCount,
        serviceName: serviceMap.get(m.a.serviceId)?.name ?? 'Service',
        pending: isOptimistic(m.a),
      }));
      const takings = mine
        .filter((m) => m.a.status === 'completed')
        .reduce((s, m) => s + m.a.priceQuoted, 0);

      // WS7: this member's off/working-band/break state for the viewed date (resolved once above).
      const cal =
        staffCalendar.get(member.id) ??
        ({ off: false, offLabel: null, band: null, break_: null } as const);

      return {
        staff: member,
        accent: chairAccent(i),
        takings,
        items,
        gaps: [] as DiaryColumn['gaps'],
        off: cal.off ? { label: cal.offLabel ?? 'Off' } : null,
        band: cal.band,
        break_: cal.break_,
      };
    });
  }, [chairs, appts, serviceMap, tz, staffCalendar]);

  const geometry = useMemo(() => {
    const spans = allColumns.flatMap((c) =>
      c.items.map((i) => ({ startMin: i.startMin, endMin: i.endMin })),
    );
    // WS7: let custom-hours bands stretch the visible window too, so an early/late adjusted shift is
    // fully in view (computeRange clamps to its sane band either way).
    const bands = allColumns.flatMap((c) => (c.off || !c.band ? [] : [c.band]));
    const work =
      bands.length > 0
        ? {
            startMin: Math.min(...bands.map((b) => b.startMin)),
            endMin: Math.max(...bands.map((b) => b.endMin)),
          }
        : undefined;
    return computeRange([...spans, ...bands], work);
  }, [allColumns]);

  // Gaps are derived against the visible window; only offer future gaps and only to floor managers.
  // WS7: suppress bookable gaps entirely when the salon is closed or the staff member is off — we
  // never offer a slot the salon can't actually take. Off-column suppression also happens in the
  // grid, but clearing gaps here keeps the data model honest (and the "No bookings yet" hint off).
  const columns = useMemo<DiaryColumn[]>(() => {
    const notBefore = isToday ? nowMin : -Infinity;
    const withGaps = allColumns.map((c) => {
      // WS7: a custom-hours exception narrows the bookable window to the member's adjusted band, so we
      // never offer a gap outside the hours they've declared they're in. No band → the full window.
      const gapStart = c.band ? Math.max(geometry.startMin, c.band.startMin) : geometry.startMin;
      const gapEnd = c.band ? Math.min(geometry.endMin, c.band.endMin) : geometry.endMin;
      return {
        ...c,
        gaps:
          isClosed || c.off
            ? ([] as DiaryColumn['gaps'])
            : findGaps(
                c.items.map((i) => ({ startMin: i.startMin, endMin: i.endMin })),
                gapStart,
                gapEnd,
                MIN_OFFERABLE_GAP,
                notBefore,
              ).map((g) => ({ startMin: g.startMin, endMin: g.endMin, waiting })),
      };
    });
    if (view === 'staff') {
      const id = focusStaffId ?? chairs[0]?.id;
      return withGaps.filter((c) => c.staff.id === id);
    }
    return withGaps;
  }, [allColumns, geometry, isToday, nowMin, waiting, view, focusStaffId, chairs, isClosed]);

  function onMove(appointmentId: string, change: MoveChange) {
    updateAppt.mutate({
      id: appointmentId,
      body: {
        staffId: change.staffId,
        startAt: wallMinutesToUtcIso(date, change.startMin, tz),
        endAt: wallMinutesToUtcIso(date, change.endMin, tz),
      },
    });
  }

  return (
    <>
      <div className="topbar">
        <div className="topbar__left">
          <div style={{ display: 'flex', gap: 6 }}>
            <IconButton label="Previous day" onClick={() => setDate((d) => shiftDate(d, -1))}>
              ‹
            </IconButton>
            <IconButton label="Next day" onClick={() => setDate((d) => shiftDate(d, 1))}>
              ›
            </IconButton>
          </div>
          <div className="topbar__title">{longDate(date, tz)}</div>
          {!isToday ? (
            <Button variant="outline" size="sm" onClick={() => setDate(today)}>
              Today
            </Button>
          ) : null}
          {/* WS7: how many clients are waiting / hold an offer for this day. Renders nothing on quiet
              days; RLS-scoped so a renter sees only their own count. */}
          <WaitlistDepthBadge date={date} />
        </div>
        <div className="topbar__right">
          <SegmentedControl<View>
            options={[
              { value: 'day', label: 'Day' },
              { value: 'week', label: 'Week' },
              { value: 'staff', label: 'Staff' },
            ]}
            value={view}
            onChange={setView}
          />
          {view === 'staff' ? (
            <Select
              value={focusStaffId ?? chairs[0]?.id ?? ''}
              onChange={(e) => setFocusStaffId(e.target.value)}
              style={{ width: 180 }}
            >
              {chairs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </Select>
          ) : (
            <DiarySearch />
          )}
          {/* WS7: "the day is full — join the waitlist". Always available; prefilled with the viewed
              day (and the focused stylist in staff view). A cancellation auto-offers the freed slot. */}
          <JoinWaitlistButton
            staff={chairs}
            services={services ?? []}
            tz={tz}
            defaultDate={date}
            defaultStaffId={view === 'staff' ? (focusStaffId ?? chairs[0]?.id ?? null) : null}
            variant="outline"
            size="md"
            label="Join waitlist"
          />
          <Button
            onClick={() => setNewOpen(true)}
            disabled={isClosed || bookableChairs.length === 0}
            title={
              isClosed
                ? 'The salon is closed on this date.'
                : bookableChairs.length === 0
                  ? 'No staff are in on this date.'
                  : undefined
            }
          >
            New appointment
          </Button>
        </div>
      </div>

      <div className="diary">
        {view === 'week' ? (
          <WeekStrip
            anchor={date}
            tz={tz}
            onPickDay={(d) => {
              setDate(d);
              setView('day');
            }}
          />
        ) : (
          <DiaryGrid
            geometry={geometry}
            columns={columns}
            nowMin={nowMin}
            showNow={isToday}
            canManageFloor={isFloorManager}
            closedBanner={isClosed ? { reason: calendar.closedDate?.reason ?? null } : null}
            onMove={onMove}
            onOpen={setOpenAppt}
            onOfferGap={(staffId, startMin, endMin) => setGapTarget({ staffId, startMin, endMin })}
            onBlockOut={isFloorManager ? setBlockOutStaffId : undefined}
          />
        )}
        <QueueRail
          salonSlug={salon?.slug ?? ''}
          filledToday={
            appts.filter(
              (a) => a.queueTicketId != null || a.source === 'queue' || a.source === 'walk_in',
            ).length
          }
        />
      </div>

      {newOpen && bookableChairs.length > 0 ? (
        <NewAppointmentModal
          date={date}
          tz={tz}
          chairs={bookableChairs}
          services={services ?? []}
          onClose={() => setNewOpen(false)}
        />
      ) : null}

      {gapTarget ? (
        <OfferGapModal
          date={date}
          tz={tz}
          target={gapTarget}
          services={services ?? []}
          onClose={() => setGapTarget(null)}
        />
      ) : null}

      {openAppt ? (
        <AppointmentModal
          date={date}
          tz={tz}
          appt={openAppt}
          service={serviceMap.get(openAppt.serviceId)}
          onClose={() => setOpenAppt(null)}
        />
      ) : null}

      {blockOutStaffId
        ? (() => {
            const member = chairs.find((c) => c.id === blockOutStaffId);
            return member ? (
              <BlockOutModal
                date={date}
                tz={tz}
                staff={member}
                onClose={() => setBlockOutStaffId(null)}
              />
            ) : null;
          })()
        : null}
    </>
  );
}

function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function DiarySearch() {
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (q.trim()) void navigate({ to: '/clients', search: { q: q.trim() } as never });
      }}
    >
      <TextInput
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search clients…"
        style={{ width: 200, borderRadius: 100, padding: '9px 16px' }}
      />
    </form>
  );
}
